import { SxProp, View } from 'dripsy';
import { t } from 'i18next';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useNavigation } from '@react-navigation/native';
import React, { useContext, useState } from 'react';

import { UniButton } from '@/design-system/components/buttons';
import { H5, H6, TextViewProps } from '@/design-system/components/typography';
import { EarnStackParamList, EarnStackRoutes } from '../..';
import { SubscribeStackRoutes, SubscribeStackScreenProps } from './type';
import { Icon } from '@/design-system/components/icons/Icon';
import { LayoutContext } from 'contexts/LayoutContext';
import { AmountSummary } from '../../components/cards/AmountSummary';
import { UniProgressCard, UniTextDetailsCard } from '@/design-system/components/cards';
import { CurrenciesIcon } from '../../components';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { CurrenciesContext } from '@/currencies/currency.context';

export const SubscribeResult: React.FC<SubscribeStackScreenProps<SubscribeStackRoutes.Result>> = ({ navigation, route }) => {
  const earnNavigation = useNavigation<NativeStackNavigationProp<EarnStackParamList>>();
  const { offer, preview, textDetailsCardData, estimatedApy, subscriptionAmount, asset, progressCardData, onLayout } = route.params;
  const { currenciesDict } = useContext(CurrenciesContext);

  const layout = useContext(LayoutContext);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const titleProps: TextViewProps & { sx: SxProp } = {
    bold: true,
    sx: {
      mt: '$3',
      textAlign: 'center'
    }
  };

  let styles = layout.isMobile ? nativeStyles : webStyles;

  const tenor = getEarnTenorTranslationKey(offer.tenor);
  const proceeds = preview.proceeds;

  return (
    <View
      sx={{
        height: layout.isMobile ? '100vh' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        padding: layout.isMobile ? '$4' : '$7',
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout;
        setDimensions({ width, height });
        onLayout(event);
      }}>
      <View
        sx={{
          height: layout.isMobile ? dimensions.height - 32 : 'auto',
          justifyContent: layout.isMobile ? 'space-between' : 'flex-start'
        }}>
        <View sx={{ gap: '$5' }}>
          <View sx={styles.containerTop}>
            <Icon name="successSpecial" height={68} width={68} />
            <Header {...titleProps}>{t('earn-invest::subscription-placed.header--subscription-placed')}</Header>
          </View>
          <CurrenciesIcon asset={asset} />
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <AmountSummary
              label={t('earn-invest::glossary.subscription-amount')}
              value={subscriptionAmount}
              asset={asset}
              amountSx={{ color: '$secondary' }}
            />
            <AmountSummary
              label={t('earn-invest::glossary.est-apy')}
              value={estimatedApy.toString()}
              asset="%"
              amountSx={{ color: '$textPrimary' }}
              alignRight
            />
          </View>
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {offer.tenor ? (
              <AmountSummary
                label={t('earn-invest::glossary.interests')}
                value={formatAmountByDecimals({
                  value: preview.estimatedRewardsAfterMaturity,
                  decimals: currenciesDict?.[offer.asset]?.decimals
                })}
                asset={offer.asset}
                amountSx={{ color: '$success' }}
                success
              />
            ) : (
              <View />
            )}
            <AmountSummary
              label={t('earn-invest::glossary.tenor')}
              value={tenor.key ? t(tenor.key, tenor.options) : ''}
              asset=""
              amountSx={{ color: '$textPrimary' }}
              alignRight
            />
          </View>
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {offer.tenor ? (
              <AmountSummary
                label={t('earn-invest::glossary.proceeds-at-maturity')}
                value={preview.proceeds}
                asset={offer.asset}
                amountSx={{ color: '$secondary' }}
                success
              />
            ) : (
              <View />
            )}
          </View>
          <UniTextDetailsCard data={textDetailsCardData} sx={styles.textDetailsCard} />
          <UniProgressCard sx={styles.progressCard} statusData={progressCardData} />
        </View>
        <View sx={{}}>
          <UniButton
            onPress={() => earnNavigation.replace(EarnStackRoutes.Main)}
            label={t('earn-invest::glossary.back-to-overview')}
            size="default"
          />
        </View>
      </View>
    </View>
  );
};

const Header: React.FC<unknown> = (props) => {
  const layout = useContext(LayoutContext);
  return layout.isMobile ? <H6 {...props} /> : <H5 {...props} />;
};

enum StyleName {
  container = 'container',
  containerTop = 'containerTop',
  textDetailsCard = 'textDetailsCard',
  progressCard = 'progressCard'
}

const baseStyles: Record<StyleName, SxProp> = {
  container: {},
  containerTop: {
    paddingX: '$5',
    pt: '$6',
    pb: '$5',
    alignItems: 'center'
  },
  textDetailsCard: {},
  progressCard: {}
};

const webStyles: Record<StyleName, SxProp> = {
  ...baseStyles,
  container: { padding: '$7' },
  progressCard: { mb: '$10', paddingX: '$4', paddingY: '$3', backgroundColor: '$backgroundSecondary', borderRadius: 10 }
};

const nativeStyles: Record<StyleName, SxProp> = {
  ...baseStyles,
  container: { paddingX: '$4', paddingY: '$5' },
  progressCard: { mb: '$10', paddingX: '$4', paddingY: '$3', backgroundColor: '$backgroundSecondary', borderRadius: 10 }
};
